import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { makeStyles } from 'tss-react/mui';
import EDButton from '../../../../../../../BaseComponents/Buttons/Button/EDButton';
import EDIcon from '../../../../../../../BaseComponents/Icon/EDIcon';
import FakturaDelContext from '../FakturaDelContextManager/FakturaDelContext';
import AttestMessageContext from '../AttestMessageContext/AttestMessageContext';
import { FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All } from '../../Queries/_QueryTypes/FakturaDelQuery';
import { EDAutoCompleteEdit } from '../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import {
  TEDLRAttestmeddelandeMOTTAGAR_TYPEnum,
  TEDLRFakturaAttestSTATUSEnum,
} from '../../../../../../../globalQueryTypes';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import mergeSort from '../../../../../../../Utils/mergeSort';
import { sortFakturattestByAttestant } from '../../Utils/FakturadelUtils';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import {
  useIsMediumBreakpoint,
  useIsSmallBreakpoint,
} from '../../../../../../../Components/BreakpointContext/BreakpointContextUtils';
import EDIconButton from '../../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import { FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All } from '../../Queries/_QueryTypes/FakturaDelType';
import { generateMessageID } from '../../../LeverantorFaktura/Utils/AttestMeddelandeUtils';

interface IProps {}

const useStyles = makeStyles()(theme => ({
  inputRoot: {
    '&[class*="MuiOutlinedInput-root"]': {
      background: theme.palette.background.default,
      [theme.breakpoints.up('md')]: {
        background: 'initial',
        padding: theme.spacing(0.25, 1, 0.25, 1),
        '&:hover:not(.Mui-disabled)': {
          background: theme.palette.background.default,
        },
      },
    },
  },
}));

const defaultValue = {
  id: 'default',
  Attestant: 'Alla Attestanter',
  _Associations: {},
} as FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All;

export const economypersonelMessageValue = {
  id: 'admin',
  Attestant: 'Ekonomipersonal',
  _Associations: {},
} as FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All;

export const AttestMessageButtons: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const fakturaDelContext = useContext(FakturaDelContext);
  const addAttestMessage = fakturaDelContext.addAttestMessage;
  const attestMessageContext = useContext(AttestMessageContext);
  const setMessageEditModeID = attestMessageContext.setMessageEditModeID;
  const setMessageReciverID = attestMessageContext.setMessageReciverID;
  const formContext = useContext(BusinessDataFormContext);
  const { classes } = useStyles();
  const isSmall = useIsSmallBreakpoint();
  const isMedium = useIsMediumBreakpoint();
  const [disableOnInitialRender, setDisableOnInitialRender] =
    useState<boolean>(true);
  const [value, setValue] =
    useState<FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All | null>(
      defaultValue
    );

  let certifiers = mergeSort(
    [
      ...fakturaDelContext.getAllAttestanter(),
      defaultValue,
      economypersonelMessageValue,
    ].filter(
      certifier =>
        certifier.id !==
          fakturaDelContext?.minFakturaAttest?.aktuellFakturaAttest?.id &&
        certifier.STATUS !== TEDLRFakturaAttestSTATUSEnum.ATTESTERAD
    ),
    (a, b) => {
      if (a.Attestant === 'Alla Attestanter') {
        return -1;
      }
      if (b.Attestant === 'Alla Attestanter') {
        return 1;
      }
      if (a.Attestant === 'Ekonomipersonal') {
        return -1;
      }
      if (b.Attestant === 'Ekonomipersonal') {
        return 1;
      }
      return sortFakturattestByAttestant(a, b);
    }
  );

  const attesterar = fakturaDelContext.jagAttesterar;

  const messages: FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All[] =
    useMemo(
      () => fakturaDelContext.attestMessages.reverse(),
      [fakturaDelContext.attestMessages]
    );

  const currentMessage = useMemo(
    () =>
      messages.find(
        (
          message: FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All
        ) => message.id === attestMessageContext.messageEditModeID
      ),
    [attestMessageContext.messageEditModeID, messages]
  );

  const lastMessage = useMemo(() => messages?.[0], [messages]);

  const isDisabled = useMemo(
    () =>
      (currentMessage !== undefined && currentMessage.MEDDELANDE === '') ||
      (lastMessage !== undefined && lastMessage.MEDDELANDE === ''),
    [currentMessage, lastMessage]
  );
  const onClick = useCallback(() => {
    const generatedID = generateMessageID();
    const isEconomypersonelSelected =
      attestMessageContext.messageReciverID === economypersonelMessageValue.id;

    const reciverID = isEconomypersonelSelected
      ? undefined
      : attestMessageContext.messageReciverID;

    const mottagarTyp = isEconomypersonelSelected
      ? TEDLRAttestmeddelandeMOTTAGAR_TYPEnum.ADMINISTRATOR
      : undefined;

    setMessageEditModeID(generatedID);
    addAttestMessage(
      '',
      generatedID,
      new Date().toLocaleTimeString('sv-SE'),
      reciverID,
      mottagarTyp
    );
  }, [
    attestMessageContext.messageReciverID,
    setMessageEditModeID,
    addAttestMessage,
  ]);

  useEffect(() => {
    if (disableOnInitialRender) {
      setTimeout(() => {
        setDisableOnInitialRender(false);
      }, 5);
    }
  }, [disableOnInitialRender]);

  useEffect(() => {
    setMessageReciverID(undefined);
    setValue(defaultValue);
  }, [setMessageReciverID, formContext?.data]);

  return (
    <EDBox
      component="span"
      sx={theme => ({
        display: 'flex',
        flexFlow: 'row nowrap',
        justifyContent: 'flex-end',
        alignItems: 'center',
        flex: '1 1 50%',
        width: '100%',
        marginTop: theme.spacing(1.2),
        boxSizing: 'content-box',
      })}
    >
      <EDBox
        sx={theme => ({
          marginX: theme.spacing(1),
          marginY: theme.spacing(0.7),
          width: '20%',
          minWidth: '125px',
          [theme.breakpoints.down('md')]: {
            width: '100%',
            minWidth: 'auto',
          },
        })}
      >
        <EDAutoCompleteEdit<FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All>
          disabled={
            disableOnInitialRender ||
            isDisabled ||
            !attesterar ||
            (formContext?.data &&
              (formContext?.formState.loading || formContext?.mutationLoading))
          }
          label="Rikta meddelandet till"
          getOptionLabel={option => {
            return option.Attestant ?? '';
          }}
          onChange={value => {
            if (!Array.isArray(value)) {
              if (value?.Attestant === economypersonelMessageValue.Attestant) {
                attestMessageContext.setMessageReciverID(
                  economypersonelMessageValue.id
                );
              } else {
                attestMessageContext.setMessageReciverID(
                  value?._Associations?.Attestratt?.id ?? undefined
                );
              }
              setValue(value);
            }
          }}
          getOptionSelected={(option, value) => option.id === value.id}
          options={certifiers}
          defaultValue={defaultValue}
          fullWidth
          hideClear
          inputMargin={'none'}
          topMargin={'dense'}
          classes={{
            inputRoot: classes.inputRoot,
          }}
          hideOutline
          value={value}
          testId="reciverOfMessageAutoComplete"
        />
      </EDBox>
      <EDBox sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
        {isSmall ? (
          <EDIconButton
            color="primary"
            disabled={isDisabled || !attesterar}
            icon={
              <EDIcon
                color={isDisabled || !attesterar ? 'disabled' : 'primary'}
                iconType="add"
              />
            }
            margin="none"
            onClick={onClick}
            padding="dense"
            tooltip="Lägg till meddelande"
            testId="addMessageButton"
          />
        ) : isMedium ? (
          <EDButton
            color="primary"
            disabled={isDisabled || !attesterar}
            flat
            icon={
              <EDIcon
                color={isDisabled || !attesterar ? 'disabled' : 'primary'}
                iconType="add"
              />
            }
            label="Lägg till"
            onClick={onClick}
            size="medium"
            tooltip="Lägg till meddelande"
            testId="addMessageButton"
          />
        ) : (
          <EDButton
            color="primary"
            disabled={isDisabled || !attesterar}
            flat
            icon={
              <EDIcon
                color={isDisabled || !attesterar ? 'disabled' : 'primary'}
                iconType="add"
              />
            }
            label={'Lägg till meddelande'}
            onClick={onClick}
            size="medium"
            tooltip="Lägg till meddelande"
            testId="addMessageButton"
          />
        )}
      </EDBox>
    </EDBox>
  );
};

export default AttestMessageButtons;
